import { computed, ref, watch } from "vue";
import { createSharedComposable } from "@vueuse/core";
import {
  collection,
  doc,
  FieldPath,
  getDocFromServer,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
  type Unsubscribe,
} from "firebase/firestore";
import { db } from "@/fire";
import { user } from "@/init";
import {
  hcpFile,
  isUnseen,
  type HcpFile,
  type HcpGroup,
  type HcpViewedVersion,
} from "./inbox";

export const useHcpInbox = createSharedComposable(() => {
  const groupsById = ref<Record<string, HcpGroup>>({});
  const viewed = ref<Record<string, HcpViewedVersion>>({});
  const loading = ref(true);
  const error = ref(false);

  watch(
    () => user.value?.uid,
    (uid, _, onCleanup) => {
      groupsById.value = {};
      viewed.value = {};
      loading.value = !!uid;
      error.value = false;
      if (!uid) return;
      let active = true;
      const documentListeners = new Map<string, Unsubscribe>();
      const pendingDocuments = new Set<string>();
      let workspacesLoaded = false;
      let viewedLoaded = false;
      const updateLoading = () => {
        loading.value =
          !workspacesLoaded || !viewedLoaded || pendingDocuments.size > 0;
      };
      const stopWorkspaces = onSnapshot(
        query(
          collection(db, "pvworkspaces"),
          where(new FieldPath("members", uid), ">", 0),
        ),
        (snapshot) => {
          if (!active) return;
          const current = new Set<string>();
          for (const workspace of snapshot.docs) {
            const data = workspace.data();
            if (data.isHcpUploadWorkspace !== true) continue;
            current.add(workspace.id);
            const previous = groupsById.value[workspace.id];
            groupsById.value[workspace.id] = {
              id: workspace.id,
              name: data.name,
              sourceDeleted: data.hcpUploadSourceDeletedAt != null,
              files: previous?.files ?? [],
            };
            if (documentListeners.has(workspace.id)) continue;
            pendingDocuments.add(workspace.id);
            documentListeners.set(
              workspace.id,
              onSnapshot(
                collection(workspace.ref, "pvdocs"),
                (files) => {
                  if (!active || !groupsById.value[workspace.id]) return;
                  pendingDocuments.delete(workspace.id);
                  updateLoading();
                  groupsById.value[workspace.id].files = files.docs
                    .map((file) => hcpFile(file.id, workspace.id, file.data()))
                    .filter((file): file is HcpFile => file !== null)
                    .sort(
                      (a, b) =>
                        b.uploadedAt.localeCompare(a.uploadedAt) ||
                        a.id.localeCompare(b.id),
                    );
                },
                () => {
                  if (!active) return;
                  delete groupsById.value[workspace.id];
                  documentListeners.delete(workspace.id);
                  pendingDocuments.delete(workspace.id);
                  updateLoading();
                  error.value = true;
                },
              ),
            );
          }
          for (const [id, stop] of documentListeners) {
            if (current.has(id)) continue;
            stop();
            documentListeners.delete(id);
            pendingDocuments.delete(id);
            delete groupsById.value[id];
          }
          workspacesLoaded = true;
          updateLoading();
        },
        () => {
          if (!active) return;
          documentListeners.forEach((stop) => stop());
          documentListeners.clear();
          groupsById.value = {};
          loading.value = false;
          error.value = true;
        },
      );
      const stopViewed = onSnapshot(
        collection(db, "pvusers", uid, "hcpViewed"),
        (snapshot) => {
          if (!active) return;
          viewedLoaded = true;
          updateLoading();
          viewed.value = Object.fromEntries(
            snapshot.docs.map((value) => [
              value.id,
              value.data() as HcpViewedVersion,
            ]),
          );
        },
        () => {
          if (active) {
            viewed.value = {};
            viewedLoaded = true;
            updateLoading();
            error.value = true;
          }
        },
      );
      onCleanup(() => {
        active = false;
        stopWorkspaces();
        stopViewed();
        documentListeners.forEach((stop) => stop());
      });
    },
    { immediate: true },
  );

  const groups = computed(() =>
    Object.values(groupsById.value)
      .filter((group) => group.files.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id)),
  );
  const unseen = (file: HcpFile) => isUnseen(file, viewed.value);
  const count = (group: HcpGroup) => group.files.filter(unseen).length;
  const total = computed(() =>
    groups.value.reduce((sum, group) => sum + count(group), 0),
  );

  async function open(file: HcpFile): Promise<HcpFile> {
    const uid = user.value?.uid;
    if (!uid) throw new Error("Signed out");
    // Read from the server again; a cached listing is not access authorization.
    const [workspace, document] = await Promise.all([
      getDocFromServer(doc(db, "pvworkspaces", file.workspaceId)),
      getDocFromServer(
        doc(db, "pvworkspaces", file.workspaceId, "pvdocs", file.id),
      ),
    ]);
    if (
      user.value?.uid !== uid ||
      workspace.data()?.isHcpUploadWorkspace !== true ||
      !(workspace.data()?.members?.[uid] > 0)
    )
      throw new Error("Access revoked");
    const current = document.exists()
      ? hcpFile(document.id, file.workspaceId, document.data())
      : null;
    if (!current) throw new Error("File unavailable");
    return current;
  }

  async function markViewed(file: HcpFile): Promise<void> {
    const uid = user.value?.uid;
    if (!uid) throw new Error("Signed out");
    await setDoc(doc(db, "pvusers", uid, "hcpViewed", file.id), {
      workspaceId: file.workspaceId,
      version: file.version,
      viewedAt: serverTimestamp(),
    });
  }

  return { groups, loading, error, total, unseen, count, open, markViewed };
});
